import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsArrowDown } from "react-icons/bs";
import PropTypes from "prop-types";
import { Button, ButtonGroup, Col, Form, Row, Tooltip, OverlayTrigger } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FaSearch } from "react-icons/fa";
import { IoHome } from "react-icons/io5";
import { RiFilterLine, RiFilterOffLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import useApiClient from "../../../hooks/apiClient";
import { paginatedViewActions } from "../../../redux/paginatedView";
import ButtonPermission from "../Button/ButtonPermission";
import TextSpinner from "../Spinner/TextSpinner";
import useDeleteItem from "./hooks/useDeleteItem";
import constants from "../../../pages/Constants";

/**
 * Componente genérico para exbição de views paginadas
 */
const SpfPaginatedView = ({
  columns,
  title,
  keyField,
  descriptionField,
  defaultSearch,
  defaultSearchPlaceholder,
  queryService,
  deleteService,
  permissionInsert,
  permissionDelete,
  history,
  createRoute,
  editRoute,
  expandComponent,
  advancedSearchFormik,
  selectableRows,
  onSelectedRowsChange,
  contextMessage,
  contextActions,
  titleBar,
  titleBarParams,
  onRowClicked,
  footer,
  children,
  reduxKey,
  stateKey,
  pageSizeRows = 10,
  buttonTitle,
  conditionalRowStyles,
  handleRowExpandToggle,
}) => {
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(pageSizeRows);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRows, setTotalRows] = useState(1);
  const [inputSearchArgument, setInputSearchArgument] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const paginatedViewState = useSelector((state) => state.paginatedView);
  const { advancedSearchParams, showAdvancedSearch } =
    history.location.state?.paginatedViewState ?? (paginatedViewState[reduxKey] || {});
  const [searchParams, setSearchParams] = useState(showAdvancedSearch ? advancedSearchParams : {});
  const [showDataTable, setShowDataTable] = useState(true);
  const [subTitle, setSubTitle] = useState("");
  const [changeSubTitle, setChangeSubTitle] = useState(true);
  const dispatch = useDispatch();

  const { request, data, errors, isLoading } = useApiClient();

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      setRows([]);
      const params = { ...searchParams, ...titleBarParams };

      request(queryService, {
        ...params,
        pageNumber,
        pageSize,
      });
    }
  }, [isFirstRender, request, queryService, searchParams, titleBarParams, pageNumber, pageSize]);

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
    setIsFirstRender(true);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
    setIsFirstRender(true);
  }, []);

  const refreshItems = useCallback(() => {
    window.location.reload(false);
  }, []);

  const { excluirConfirmModal } = useDeleteItem({
    columns,
    deleteService,
    keyField,
    descriptionField,
    permissionDelete,
    onDeleteItem: refreshItems,
  });

  useEffect(() => {
    if (data?.result) {
      if (data?.result?.length && !data?.result[0]?.totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados. `);
        return;
      }
      setRows(data?.result || []);
      setTotalRows(data?.result?.length ? data?.result[0]?.totalRows : 0);
      setSelectedRows([]);
      setToggleCleared((curr) => !curr);
    }

    if (errors) {
      toast.error(errors.message);
    }
  }, [data, errors]);

  const viewStateKey = useRef(null);
  const { setValues } = advancedSearchFormik ?? {};

  useEffect(() => {
    if (viewStateKey.current === null) {
      viewStateKey.current = stateKey ?? "none";
    } else if (stateKey && stateKey !== viewStateKey.current) {
      viewStateKey.current = stateKey;
      setPageNumber(1);
      setSearchParams((curr) => ({ ...curr }));
    }
  }, [stateKey]);

  // INIT: Carregar estado do form de busca avançada
  useEffect(() => {
    if (showAdvancedSearch) {
      setValues((curr) => ({ ...curr, ...advancedSearchParams }));
    }
  }, [setValues, showAdvancedSearch, advancedSearchParams]);

  if (expandComponent && rows.length) {
    rows.forEach((row) => {
      const expandableRow = row;
      expandableRow.setRows = setRows;
    });
  }

  useEffect(() => {
    // Atualiza o estado da busca avançada no Redux
    if (showAdvancedSearch !== undefined) {
      dispatch(
        paginatedViewActions.setShowAdvancedSearch({
          reduxKey,
          showAdvancedSearch,
        }),
      );
    }

    // Atualiza os parâmetros de pesquisa quando a busca avançada estiver ativada ou desativada
    if (showAdvancedSearch) {
      setSearchParams(advancedSearchParams);
    } else {
      setSearchParams({});
    }

    // Reinicia a páginação para a página inicial quando os parâmetros de busca são alterados
    if (advancedSearchParams) {
      setPageNumber(1);
    }
  }, [dispatch, reduxKey, advancedSearchParams, showAdvancedSearch]);

  useEffect(() => {
    if (showDataTable && changeSubTitle) {
      setSubTitle("[Acesso rápido]");
    } else {
      setSubTitle("[Busca Avançada]");
    }
  }, [showDataTable, changeSubTitle]);

  return (
    <>
      <Row hidden={reduxKey === "procedimentoFiscal" ? title : !title}>
        <Col sm="12">
          <h1 className="h3 mb-4 text-gray-800">{`${title} `}</h1>
        </Col>
      </Row>
      {excluirConfirmModal}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            {defaultSearch && (
              <div className="card-header pt-3 pb-0 flex-row w-100">
                <Form.Group as={Row} className="d-flex flex-row">
                  <h6 className="font-weight-bold text-primary flex-grow-1 ml-2 mt-2">
                    {title ? `Consulta de ${title}  ${reduxKey === "procedimentoFiscal" && subTitle}` : ""}
                  </h6>
                  {titleBar ??
                    (permissionInsert && (
                      <>
                        <ButtonPermission
                          permission={permissionInsert}
                          className="btn btn-primary btn-md btn-user"
                          onClick={() => history.push(createRoute || editRoute)}
                          name={!buttonTitle ? "Novo" : buttonTitle}
                        />
                        <div className="card-divider d-none d-sm-block" />
                      </>
                    ))}
                  {!showAdvancedSearch && reduxKey !== "procedimentoFiscal" && (
                    <ButtonGroup>
                      <Form.Control
                        className="w-auto border-top-right-0 border-bottom-right-0"
                        type="text"
                        placeholder={defaultSearchPlaceholder}
                        value={inputSearchArgument}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            setPageNumber(1);
                            setSearchParams({ searchArgument: inputSearchArgument });
                            setIsFirstRender(true);
                          }
                        }}
                        onChange={(e) => setInputSearchArgument(e.target.value)}
                      />
                      <Button
                        className="btn-search-inside btn-md mr-3"
                        onClick={() => {
                          setPageNumber(1);
                          setSearchParams({ searchArgument: inputSearchArgument });
                          setIsFirstRender(true);
                        }}
                      >
                        <FaSearch />
                      </Button>
                    </ButtonGroup>
                  )}
                  {advancedSearchFormik && (
                    <>
                      <OverlayTrigger placement="top" overlay={<Tooltip>Busca avançada</Tooltip>}>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          onClick={() => {
                            // Recupera o estado mais recente diretamente do Redux
                            const currentShowAdvancedSearch = paginatedViewState[reduxKey]?.showAdvancedSearch || false;
                            setShowDataTable(false);
                            if (!showDataTable) {
                              advancedSearchFormik.resetForm();
                            }
                            // Alterna o estado
                            dispatch(
                              paginatedViewActions.setShowAdvancedSearch({
                                reduxKey,
                                showAdvancedSearch: !currentShowAdvancedSearch,
                              }),
                            );
                          }}
                        >
                          {showAdvancedSearch ? <RiFilterOffLine /> : <RiFilterLine />}
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger placement="top" overlay={<Tooltip>Listagem Geral</Tooltip>}>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          onClick={() => {
                            setShowDataTable(true);
                            setChangeSubTitle(true);
                            setIsFirstRender(true);
                            setPageNumber(1);
                            advancedSearchFormik.resetForm();
                            setSearchParams({});
                            dispatch(paginatedViewActions.saveParams({ reduxKey, params: {} }));
                            dispatch(
                              paginatedViewActions.setShowAdvancedSearch({
                                reduxKey,
                                showAdvancedSearch: false,
                              }),
                            );
                          }}
                        >
                          <IoHome />
                        </Button>
                      </OverlayTrigger>
                    </>
                  )}
                </Form.Group>
                <br />
                {paginatedViewState[reduxKey]?.showAdvancedSearch && (
                  <>
                    <hr />
                    {children}
                    <Form.Group as={Row} className="d-flex flex-row">
                      <Col sm="12">
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          onClick={() => {
                            setIsFirstRender(true);
                            setPageNumber(1);
                            setSearchParams({ ...advancedSearchFormik.values });
                            setShowDataTable(true);
                            setChangeSubTitle(false);
                            dispatch(
                              paginatedViewActions.saveParams({ reduxKey, params: { ...advancedSearchFormik.values } }),
                            );
                          }}
                        >
                          Buscar
                        </Button>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          variant="light"
                          onClick={() => {
                            setIsFirstRender(true);
                            setPageNumber(1);
                            advancedSearchFormik.resetForm();
                            setSearchParams({});
                            setShowDataTable(false);
                            setChangeSubTitle(false);
                            dispatch(paginatedViewActions.saveParams({ reduxKey, params: {} }));
                          }}
                        >
                          Limpar
                        </Button>
                      </Col>
                    </Form.Group>
                    <br />
                  </>
                )}
              </div>
            )}
            {showDataTable && (
              <div className="card-body">
                <DataTable
                  noHeader={!selectedRows.length}
                  keyField={keyField}
                  columns={columns}
                  data={rows}
                  progressPending={isLoading}
                  noDataComponent="Nenhum registro para exibir"
                  progressComponent={<TextSpinner />}
                  selectableRows={selectableRows || false}
                  onSelectedRowsChange={(selection) => {
                    if (typeof onSelectedRowsChange === "function") {
                      onSelectedRowsChange(selection);
                    }
                    setSelectedRows(selection.selectedRows);
                  }}
                  contextMessage={contextMessage}
                  contextActions={contextActions}
                  clearSelectedRows={toggleCleared}
                  pointerOnHover
                  onRowClicked={(item) => {
                    if (typeof onRowClicked === "function") {
                      onRowClicked(item);
                    } else if (editRoute) {
                      history.push({
                        pathname: editRoute,
                        state: { itemKey: item[keyField] },
                        paginatedViewState: paginatedViewState.procedimentoFiscal,
                      });
                    }
                  }}
                  sortable
                  sortIcon={<BsArrowDown />}
                  pagination
                  paginationServer
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationTotalRows={totalRows}
                  paginationComponentOptions={constants.paginationComponentOptions}
                  expandableRows={!!expandComponent}
                  expandableRowsComponent={expandComponent}
                  onRowExpandToggled={handleRowExpandToggle}
                  conditionalRowStyles={conditionalRowStyles}
                  handleRowExpandToggle={handleRowExpandToggle}
                />
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

SpfPaginatedView.propTypes = {
  /** Título da página */
  title: PropTypes.string,
  /** Nome da propriedade que identifica unicamente cada item da tabela */
  keyField: PropTypes.string.isRequired,
  /** Campo utilizado como referência para descrever cada item em diálogos */
  descriptionField: PropTypes.string.isRequired,
  /** Array contendo a descrição das colunas. Mais informações: https://github.com/jbetancur/react-data-table-component  */
  columns: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  /** Chave que identifica o estado do componente no Redux. **Necessária** caso utilize a busca avançada. */
  reduxKey: PropTypes.string,
};

SpfPaginatedView.defaultProps = { reduxKey: "none", title: "" };

export default SpfPaginatedView;
